import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedPortrait — gives a flat headshot a subtle "alive on a video call"
 * feel via stacked CSS animations:
 *
 *  1. breathe  — barely-perceptible scale loop (~5s)
 *  2. blink    — quick squash + darken overlay at randomised 3.5-8s intervals
 *  3. speak    — when `state === "speaking"`:
 *                · faster mouth-pulse loop OR (if `amp` is provided) a real
 *                  audio-reactive transform driven by the actual TTS amplitude
 *                · soft red glow halo
 *  4. listen   — slate ring when the user is speaking
 *
 * Props:
 *   src, alt, size            — image
 *   state                     — 'idle' | 'speaking' | 'listening'
 *   amp                       — 0..1, when provided overrides the canned
 *                                speak loop with real-time audio reactivity
 *   rounded, className
 */
export default function AnimatedPortrait({
  src,
  alt = "",
  size = 96,
  state = "idle",
  amp = 0,
  className = "",
  rounded = "rounded-full",
}) {
  const [blinkTick, setBlinkTick] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const schedule = () => {
      const ms = 3500 + Math.random() * 4500;
      timerRef.current = setTimeout(() => {
        setBlinkTick((n) => n + 1);
        schedule();
      }, ms);
    };
    schedule();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  const ringClass =
    state === "speaking" ? "ring-4 ring-[#E53935]/40 shadow-[0_0_40px_-8px_rgba(229,57,53,0.55)]"
    : state === "listening" ? "ring-4 ring-emerald-400/60 shadow-[0_0_28px_-8px_rgba(16,185,129,0.45)]"
    : "ring-2 ring-white shadow-sm";

  // Audio-reactive scaling: amp 0..1 → tiny boost on top of the breathe baseline.
  // Bump it a bit more on the "speaking" state so the pulse is visible.
  const amplitudeScale = state === "speaking" ? 1 + amp * 0.06 : 1;

  return (
    <div
      className={`relative inline-block animated-portrait ${rounded} ${ringClass} ${className}`}
      style={{ width: size, height: size }}
      data-state={state}
      data-testid="animated-portrait"
    >
      <img
        src={src}
        alt={alt}
        key={blinkTick}
        className={`absolute inset-0 w-full h-full object-cover ${rounded} avatar-breathe ${state === "speaking" && amp <= 0.001 ? "avatar-speaking" : ""} avatar-blink`}
        style={{
          transform: `scale(${amplitudeScale})`,
          transition: "transform 60ms ease-out",
        }}
        draggable={false}
        onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
      />
      <span
        key={`overlay-${blinkTick}`}
        className="absolute inset-x-0 top-[18%] h-[22%] pointer-events-none blink-overlay"
      />
      {state === "speaking" && (
        <span className={`absolute inset-0 ${rounded} pointer-events-none avatar-speaking-ring`} />
      )}
    </div>
  );
}
